import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useUser } from './UserContext';
import { logger } from '../utils/logger';


export interface Tenant {
  id: number;
  slug: string;
  name: string;
  is_active: boolean;
}

interface TenantContextType {
  currentTenant: Tenant | null;
  availableTenants: Tenant[];
  loading: boolean;
  error: string | null;
  setCurrentTenant: (tenant: Tenant) => void;
  refreshTenants: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

/**
 * Hook to access tenant context
 */
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    // Return a safe default instead of throwing immediately
    // This prevents white screen errors during initial load
    // Note: loading is FALSE here to prevent infinite re-renders
    logger.warn('useTenant called outside of TenantProvider, using default values');
    return {
      currentTenant: null,
      availableTenants: [],
      loading: false,
      error: 'TenantProvider not available',
      setCurrentTenant: () => {},
      refreshTenants: async () => {}
    };
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider = ({ children }: TenantProviderProps) => {
  const { user } = useUser();
  const [currentTenant, setCurrentTenantState] = useState<Tenant | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch tenants available to the current user
   */
  const fetchAvailableTenants = async () => {
    try {
      // ✅ httpOnly cookie is sent automatically
      let tenants: Tenant[] = [];

      const response = await fetch('/routes/users/me/tenants', {
        credentials: 'include',  // ✅ Send httpOnly cookie
      });

      if (response.ok) {
        tenants = await response.json();
      } else {
        // Fallback: If endpoint doesn't exist yet, assume default tenant
        logger.warn('User tenants endpoint not available, using default tenant');
        tenants = [{
          id: 1,
          slug: 'default',
          name: 'Default Tenant',
          is_active: true
        }];
      }

      setAvailableTenants(tenants);

      // Set current tenant from localStorage or use first available
      // NOTE: Client can manipulate current_tenant_id in localStorage.
      // SECURITY: Backend MUST validate that user has access to this tenant!
      const savedTenantId = localStorage.getItem('current_tenant_id');
      let selected = tenants.find(t => t.id === Number(savedTenantId));

      if (!selected && tenants.length > 0) {
        selected = tenants[0];
      }

      if (selected) {
        setCurrentTenantState(selected);
        localStorage.setItem('current_tenant_id', selected.id.toString());
      }

      setError(null);
    } catch (err) {
      logger.error('Error fetching tenants:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tenants');

      // Fallback to default tenant
      const defaultTenant: Tenant = {
        id: 1,
        slug: 'default',
        name: 'Default Tenant',
        is_active: true
      };
      setAvailableTenants([defaultTenant]);
      setCurrentTenantState(defaultTenant);
      localStorage.setItem('current_tenant_id', '1');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Set current tenant and persist to localStorage
   */
  const setCurrentTenant = (tenant: Tenant) => {
    setCurrentTenantState(tenant);
    localStorage.setItem('current_tenant_id', tenant.id.toString());
  };

  /**
   * Refresh tenant list
   */
  const refreshTenants = async () => {
    setLoading(true);
    await fetchAvailableTenants();
  };

  // Fetch tenants when component mounts
  useEffect(() => {
    if (user) {
      fetchAvailableTenants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        availableTenants,
        loading,
        error,
        setCurrentTenant,
        refreshTenants
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};
